import React from 'react';
import { cwd } from 'node:process';
import { getUserInfo } from '@wix/cli-auth';
import {
  getDebugLogFilePath,
  getUserFeedbackCacheFilePath,
  SENTRY_DSN,
} from '@wix/cli-core-definitions';
import {
  createErrorHandler,
  OutdatedVersionMessage,
} from '@wix/cli-error-reporting';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { renderErrorSync } from '@wix/cli-ui-kit';
import { writeCrashReport } from '@wix/cli-debug-log';
import { setupTestEnv } from '@wix/cli-test-overrides';
import { versionNotifier } from '@wix/cli-version-manager';
import { NO_TTY_JSON_OUTPUT_OPTION } from '@wix/cli-command-contract';
import { createPanorama } from '@wix/cli-panorama';
import { randomUUID } from 'node:crypto';
import { determineAgent } from '@vercel/detect-agent';
import packageJson from '../package.json' with { type: 'json' };
import { createBiLogger } from './bi.js';
import { addCommandInfoToErrorReports } from './commander-hooks.js';
import { createProgram } from './program.js';

setupTestEnv();

const projectFolder = cwd();

function setupErrorHandling() {
  const {
    errorReporter,
    handler: errorHandler,
    registerToProcessErrors,
  } = createErrorHandler({
    debugOutputPath: getDebugLogFilePath(projectFolder),
    dsn: SENTRY_DSN,
    release: packageJson.version,
    userFeedbackCachePath: getUserFeedbackCacheFilePath(projectFolder),
  });

  registerToProcessErrors();

  return { errorHandler, errorReporter };
}

// Please do not add anything before this line, to make sure we catch all errors
const { errorHandler, errorReporter } = setupErrorHandling();

try {
  const userInfo = await getUserInfo();

  if (userInfo) {
    errorReporter.setUser({ id: userInfo.userId });
  }

  const cliSessionId = randomUUID();
  errorReporter.setCliSessionId(cliSessionId);

  const defaultAgentDetectionResult = {
    agent: undefined,
    isAgent: false,
  } as const;

  const aiAgent = await determineAgent().catch((error: unknown) => {
    errorReporter.reportError(error);
    return defaultAgentDetectionResult;
  });

  const aiAgentName = aiAgent.agent?.name;
  const loginMode = aiAgent.isAgent ? 'agent' : 'interactive';
  if (aiAgentName != null) {
    errorReporter.setAiAgentName(aiAgentName);
  }

  const { biLogger } = await createBiLogger(
    cliSessionId,
    errorReporter,
    userInfo?.userId,
    aiAgentName
  );

  const panorama = await createPanorama({
    artifact: packageJson.wix.artifact,
    version: packageJson.version,
    errorReporter,
    userId: userInfo?.userId,
    cliSessionId,
    aiAgentName,
  });

  const packageDetails = await versionNotifier(packageJson).catch(
    (error: unknown) => {
      errorReporter.reportError(error);
      writeCrashReport(getDebugLogFilePath(cwd()), error, new Date());
    }
  );

  const { program, hasCliAppInstalled } = await createProgram({
    biLogger,
    errorReporter,
    loginMode,
    projectFolder,
    panorama,
  });

  if (packageDetails?.deprecated) {
    throw new CliError({
      code: CliErrorCode.DeprecatedVersion({
        ...packageDetails,
        hasCliAppInstalled,
      }),
      cause: undefined,
    });
  }

  addCommandInfoToErrorReports(program, { errorReporter });
  const actionCommand = await program.parseAsync();

  if (
    packageDetails?.newestVersion === false &&
    process.stderr.isTTY === true &&
    !actionCommand.args.includes(NO_TTY_JSON_OUTPUT_OPTION)
  ) {
    // stderr, so the notice never pollutes captured/piped command output
    // (e.g. `TOKEN=$(wix token)` was getting the box glued onto the token)
    renderErrorSync(
      <OutdatedVersionMessage
        currentVersion={packageDetails.currentVersion}
        latestVersion={packageDetails.latestVersion}
        hasCliAppInstalled={hasCliAppInstalled}
      />
    );
  }
} catch (error) {
  errorHandler({ error });
}
